import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import QRCode from "qrcode";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { useAppStore } from "@/lib/store";
import { buildSeo, SITE_URL } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  type AngleMode,
  evaluate,
  formatNumber,
  generateTable,
  solveQuadratic,
  solveCubic,
  solveLin2,
  solveLin3,
  toFractionTex,
  normalCdfRange,
  binomialPmf,
  binomialCdf,
  poissonPmf,
  tCdf,
} from "@/lib/calculators/casio-fx991ex";

type Bilingual = { en: string; bn: string };
type Mode = "COMP" | "STAT" | "TABLE" | "EQN" | "DIST" | "SHEET";
type EqnKind = "lin2" | "lin3" | "quad" | "cubic";
type DistKind = "normal" | "binomial" | "poisson" | "t";

const STR = {
  h1: { en: "Casio FX-991EX Emulator", bn: "ক্যাসিও FX-991EX এমুলেটর" },
  desc: {
    en: "Online emulator of the Casio FX-991EX (ClassWiz) — high-resolution display, distribution, spreadsheet and QR code generation.",
    bn: "ক্যাসিও FX-991EX (ClassWiz) এর অনলাইন এমুলেটর — উন্নত ডিসপ্লে, ডিস্ট্রিবিউশন, স্প্রেডশিট ও QR কোড।",
  },
  examNote: {
    en: "Practice tool only. Use a physical calculator in actual exams.",
    bn: "শুধু অনুশীলনের জন্য। প্রকৃত পরীক্ষায় ফিজিক্যাল ক্যালকুলেটর ব্যবহার করুন।",
  },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "FX-991EX vs FX-991ES?", bn: "FX-991EX বনাম FX-991ES?" },
    a: {
      en: "The FX-991EX (ClassWiz) has a higher-resolution dot-matrix display (192×63 vs 96×31), is roughly 4× faster, and adds Spreadsheet, Distribution and QR-code modes. The FX-991ES Plus is older but still widely used in Bangladesh.",
      bn: "FX-991EX (ClassWiz) এ উন্নত ডিসপ্লে (১৯২×৬৩), দ্রুত প্রসেসর এবং স্প্রেডশিট, ডিস্ট্রিবিউশন ও QR কোড মোড আছে। FX-991ES Plus পুরোনো হলেও বহুল ব্যবহৃত।",
    },
  },
  {
    q: { en: "Should I buy ClassWiz?", bn: "ClassWiz কেনা উচিত?" },
    a: {
      en: "If your budget allows and you'll need statistics, distributions or spreadsheet work (engineering, advanced HSC, university), the EX is worth it. For pure SSC/HSC math, the ES Plus is sufficient and cheaper.",
      bn: "যদি বাজেটে কুলায় ও পরিসংখ্যান/ডিস্ট্রিবিউশন/স্প্রেডশিট দরকার হয় (ইঞ্জিনিয়ারিং, উচ্চতর HSC, ইউনিভার্সিটি), EX ভালো। শুধু SSC/HSC গণিতের জন্য ES Plus যথেষ্ট ও সাশ্রয়ী।",
    },
  },
  {
    q: { en: "How to use distribution mode?", bn: "ডিস্ট্রিবিউশন মোড কীভাবে ব্যবহার করব?" },
    a: {
      en: "Open MENU, choose Distribution, then pick Normal / Binomial / Poisson / t. Enter the parameters and the calculator returns the probability. For example: Normal μ=0, σ=1, P(X ≤ 1.96) ≈ 0.975.",
      bn: "মেনু খুলে Distribution বেছে নিন → Normal / Binomial / Poisson / t। প্যারামিটার দিন, সম্ভাবনা পাবেন। যেমন: নরমাল μ=0, σ=1, P(X ≤ 1.96) ≈ 0.975।",
    },
  },
  {
    q: { en: "What is ClassWiz?", bn: "ClassWiz কী?" },
    a: {
      en: "ClassWiz is Casio's product line introduced in 2014 with a higher-resolution display, faster CPU and an icon-based menu. The FX-991EX is its flagship scientific model.",
      bn: "ClassWiz হল ক্যাসিওর ২০১৪ সালের নতুন সিরিজ — উন্নত ডিসপ্লে, দ্রুত CPU ও আইকনভিত্তিক মেনু। FX-991EX এর ফ্ল্যাগশিপ মডেল।",
    },
  },
  {
    q: { en: "Does QR code feature work offline?", bn: "QR কোড অফলাইনে কাজ করে?" },
    a: {
      en: "Yes. QR generation runs locally in your browser using the qrcode library — no network call is made. The encoded data points back to this page so you can share results.",
      bn: "হ্যাঁ। QR কোড আপনার ব্রাউজারে লোকালি তৈরি হয় — কোনো ইন্টারনেট কল হয় না।",
    },
  },
  {
    q: { en: "Can I use this in admission tests?", bn: "ভর্তি পরীক্ষায় ব্যবহার করা যাবে?" },
    a: {
      en: "No — admission tests (DU, BUET, MBBS) require a physical non-programmable calculator. The FX-991EX is on the approved list for many universities; check your specific test guidelines.",
      bn: "না — ভর্তি পরীক্ষায় ফিজিক্যাল নন-প্রোগ্রামেবল ক্যালকুলেটর ব্যবহার করতে হবে। FX-991EX অনেক বিশ্ববিদ্যালয়ের অনুমোদিত তালিকায় আছে।",
    },
  },
  {
    q: { en: "What does the OPTN button do?", bn: "OPTN বোতাম কী করে?" },
    a: {
      en: "OPTN opens a context menu of options relevant to the current mode — for example, in Distribution mode it lists distribution types; in Statistics it shows summary statistics options.",
      bn: "OPTN বর্তমান মোডের প্রাসঙ্গিক বিকল্পগুলোর মেনু খোলে — যেমন Distribution মোডে ডিস্ট্রিবিউশনের তালিকা।",
    },
  },
  {
    q: { en: "ক্লাসউইজ আর সাধারণ FX এর পার্থক্য কী?", bn: "ক্লাসউইজ আর সাধারণ FX এর পার্থক্য কী?" },
    a: {
      en: "ক্লাসউইজ সিরিজে উন্নত ডট-ম্যাট্রিক্স ডিসপ্লে, দ্রুত CPU, স্প্রেডশিট ও QR কোড আছে। মেনু আইকনভিত্তিক — মোডে যেতে অনেক সহজ।",
      bn: "ক্লাসউইজ সিরিজে উন্নত ডট-ম্যাট্রিক্স ডিসপ্লে, দ্রুত CPU, স্প্রেডশিট ও QR কোড আছে। মেনু আইকনভিত্তিক — মোডে যেতে অনেক সহজ।",
    },
  },
];

export const Route = createFileRoute("/casio-fx-991ex")({
  head: () => {
    const seo = buildSeo({
      title: "Casio FX-991EX Emulator Online — Free ClassWiz",
      description:
        "Free online Casio FX-991EX (ClassWiz) emulator. High-resolution display, spreadsheet, distribution functions. For HSC, university students in Bangladesh. ক্যাসিও ক্লাসউইজ।",
      path: "/casio-fx-991ex",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Casio FX-991EX ClassWiz", path: "/casio-fx-991ex" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: CasioFx991exPage,
});

function CasioFx991exPage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  return (
    <CalculatorLayout
      slug="casio-fx-991ex"
      title={tr("h1")}
      description={tr("desc")}
      iconName="Calculator"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      <div className={cn("mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-300", lang === "bn" && "font-bn")}>
        ⚠ {tr("examNote")}
      </div>
      <ClassWizDevice />
      <Content />
    </CalculatorLayout>
  );
}

// ===========================================================================
// Device shell (ClassWiz styling — silver/blue accents, icon menu)
// ===========================================================================
function ClassWizDevice() {
  const [mode, setMode] = useState<Mode>("COMP");
  const [angle, setAngle] = useState<AngleMode>("DEG");
  const [shift, setShift] = useState(false);
  const [alpha, setAlpha] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const [buffer, setBuffer] = useState("");
  const [cursor, setCursor] = useState(0);
  const [resultDisplay, setResultDisplay] = useState("0");
  const [resultTex, setResultTex] = useState<string | null>(null);
  const [ans, setAns] = useState(0);
  const [history, setHistory] = useState<{ buf: string; res: string }[]>([]);
  const [historyIdx, setHistoryIdx] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [memory, setMemory] = useState(0);
  const [showQR, setShowQR] = useState(false);
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  const reset = useCallback(() => {
    setBuffer(""); setCursor(0); setResultDisplay("0"); setResultTex(null); setError(null); setHistoryIdx(-1);
  }, []);

  const insert = useCallback((tok: string) => {
    setError(null);
    setBuffer((b) => b.slice(0, cursor) + tok + b.slice(cursor));
    setCursor((c) => c + tok.length);
  }, [cursor]);

  const equals = useCallback(() => {
    if (!buffer.trim()) return;
    const r = evaluate(buffer, { angle, ans, vars: { A: 0, B: 0, C: 0, D: 0, X: 0, Y: 0, M: memory } });
    if (!r.ok) {
      setError(r.kind); setResultDisplay(r.kind); setResultTex(null); return;
    }
    setAns(r.value);
    setResultDisplay(r.display);
    setResultTex(r.fractionTex ?? null);
    setError(null);
    setHistory((h) => [{ buf: buffer, res: r.display }, ...h].slice(0, 6));
    setHistoryIdx(-1);
  }, [buffer, angle, ans, memory]);

  const generateQR = useCallback(async () => {
    const payload = `${SITE_URL}/casio-fx-991ex?expr=${encodeURIComponent(buffer)}&result=${encodeURIComponent(resultDisplay)}`;
    const url = await QRCode.toDataURL(payload, { margin: 1, width: 200 });
    setQrUrl(url);
    setShowQR(true);
  }, [buffer, resultDisplay]);

  const press = useCallback((label: string) => {
    if (label === "SHIFT") { setShift((s) => !s); setAlpha(false); return; }
    if (label === "ALPHA") { setAlpha((a) => !a); setShift(false); return; }
    if (label === "MENU") { setShowMenu(true); setShift(false); setAlpha(false); return; }
    if (label === "OPTN") { setShowMenu(true); return; }

    let tok = label;
    if (shift) {
      const map: Record<string, string> = {
        sin: "asin(", cos: "acos(", tan: "atan(",
        log: "10^(", ln: "e^(", "√": "³√(", "(-)": "π",
        ".": String(Math.random().toFixed(3)),
        "=": "S↔D", "AC": "OFF", "×10ˣ": "π",
      };
      if (map[label]) tok = map[label];
      setShift(false);
    }

    const display: Record<string, string> = {
      "sin": "sin(", "cos": "cos(", "tan": "tan(",
      "asin(": "asin(", "acos(": "acos(", "atan(": "atan(",
      "log": "log(", "ln": "ln(",
      "√": "√(", "³√(": "³√(",
      "□²": "^2", "□^□": "^(", "x²": "^2",
      "π": "π", "Ans": "Ans",
      "×10ˣ": "×10^", "10^(": "10^(", "e^(": "e^(",
      "(-)": "−", "-": "−", "×": "×", "÷": "÷", "+": "+",
      "(": "(", ")": ")", ",": ",", ".": ".",
    };
    const text = display[tok] ?? tok;

    if (tok === "OFF") { reset(); setResultDisplay("Goodbye"); return; }
    if (tok === "S↔D") {
      if (resultTex) setResultTex(null);
      else { const t = toFractionTex(ans); if (t) setResultTex(t); }
      return;
    }
    if (tok === "AC") { reset(); return; }
    if (tok === "DEL") {
      if (cursor === 0) return;
      setBuffer((b) => b.slice(0, cursor - 1) + b.slice(cursor));
      setCursor((c) => c - 1);
      return;
    }
    if (tok === "=") { equals(); return; }
    if (tok === "▲") {
      if (history.length === 0) return;
      const next = Math.min(history.length - 1, historyIdx + 1);
      setHistoryIdx(next); setBuffer(history[next].buf); setCursor(history[next].buf.length);
      return;
    }
    if (tok === "▼") {
      if (historyIdx <= 0) { setHistoryIdx(-1); return; }
      const next = historyIdx - 1;
      setHistoryIdx(next); setBuffer(history[next].buf); setCursor(history[next].buf.length);
      return;
    }
    if (tok === "◀") { setCursor((c) => Math.max(0, c - 1)); return; }
    if (tok === "▶") { setCursor((c) => Math.min(buffer.length, c + 1)); return; }
    if (tok === "QR") { void generateQR(); return; }
    if (tok === "M+") { setMemory((m) => m + ans); return; }
    if (tok === "STO") { setMemory(ans); return; }
    if (tok === "RCL") { insert(`${memory}`); return; }

    insert(text);
  }, [shift, equals, insert, ans, memory, history, historyIdx, cursor, buffer.length, resultTex, generateQR, reset]);

  // Keyboard
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (mode !== "COMP") return;
      const k = e.key;
      if (/^[0-9]$/.test(k)) { press(k); e.preventDefault(); return; }
      const m: Record<string, string> = {
        "+": "+", "-": "-", "*": "×", "/": "÷", ".": ".",
        "(": "(", ")": ")", "^": "□^□",
        "Enter": "=", "=": "=", "Backspace": "DEL", "Escape": "AC",
        "ArrowUp": "▲", "ArrowDown": "▼", "ArrowLeft": "◀", "ArrowRight": "▶",
        "s": "sin", "c": "cos", "t": "tan", "l": "log", "L": "ln",
        "p": "π", "r": "√",
      };
      if (m[k]) { press(m[k]); e.preventDefault(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [press, mode]);

  return (
    <div className="overflow-hidden rounded-3xl bg-gradient-to-b from-slate-300 via-slate-400 to-slate-600 p-3 shadow-2xl sm:p-5 dark:from-slate-700 dark:via-slate-800 dark:to-slate-900">
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-700 dark:text-slate-200">
        <span className="font-bold">CASIO</span>
        <span className="rounded bg-blue-700 px-2 py-0.5 text-[9px] font-bold tracking-wider text-white">ClassWiz</span>
        <span className="text-[9px] font-semibold">fx-991EX</span>
      </div>

      <HighResDisplay
        buffer={buffer} cursor={cursor} result={resultDisplay} resultTex={resultTex}
        shift={shift} alpha={alpha} angle={angle} memory={memory !== 0} error={error}
      />

      <div className="mt-2 flex flex-wrap items-center gap-1.5 px-1 text-[10px]">
        {(["DEG", "RAD", "GRAD"] as AngleMode[]).map((a) => (
          <button key={a} onClick={() => setAngle(a)}
            className={cn("rounded px-2 py-0.5 font-mono",
              angle === a ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-700 hover:bg-slate-100 dark:bg-slate-700 dark:text-slate-200")}>{a}</button>
        ))}
        <button onClick={generateQR} disabled={resultDisplay === "0"} className="ml-auto rounded bg-emerald-600 px-2 py-0.5 font-mono text-white disabled:opacity-40">QR</button>
        <span className="text-slate-700 dark:text-slate-200">mode: <span className="font-mono">{mode}</span></span>
      </div>

      {showMenu && <IconMenu current={mode} onPick={(m) => { setMode(m); setShowMenu(false); reset(); }} onClose={() => setShowMenu(false)} />}

      {showQR && qrUrl && (
        <div className="mt-3 flex items-center gap-3 rounded-md border border-slate-400 bg-white p-3 dark:border-slate-600 dark:bg-slate-800">
          <img src={qrUrl} alt="QR code with calculation" className="h-32 w-32" />
          <div className="flex-1 text-xs">
            <div className="mb-1 font-semibold text-slate-700 dark:text-slate-200">Scan to share result</div>
            <div className="font-mono text-slate-600 dark:text-slate-300 break-all">{buffer || "—"} = {resultDisplay}</div>
            <button onClick={() => setShowQR(false)} className="mt-2 rounded bg-slate-200 px-2 py-1 dark:bg-slate-700">Close</button>
          </div>
        </div>
      )}

      {mode === "COMP" && <KeyPadEX press={press} shift={shift} alpha={alpha} />}
      {mode === "EQN" && <EqnPanel onExit={() => setMode("COMP")} />}
      {mode === "TABLE" && <TablePanel angle={angle} onExit={() => setMode("COMP")} />}
      {mode === "STAT" && <StatPanel onExit={() => setMode("COMP")} />}
      {mode === "DIST" && <DistributionPanel onExit={() => setMode("COMP")} />}
      {mode === "SHEET" && <SpreadsheetPanel onExit={() => setMode("COMP")} />}

      <div className="mt-3 flex items-center justify-between px-1 text-[10px] text-slate-700 dark:text-slate-300">
        <button onClick={reset} className="rounded bg-slate-200 px-2 py-1 hover:bg-slate-100 dark:bg-slate-700 dark:hover:bg-slate-600">Reset</button>
        <span>Ans = <span className="font-mono">{formatNumber(ans)}</span></span>
      </div>
    </div>
  );
}

// ===========================================================================
// High-res display
// ===========================================================================
function HighResDisplay({
  buffer, cursor, result, resultTex, shift, alpha, angle, memory, error,
}: {
  buffer: string; cursor: number; result: string; resultTex: string | null;
  shift: boolean; alpha: boolean; angle: AngleMode; memory: boolean; error: string | null;
}) {
  const before = buffer.slice(0, cursor);
  const after = buffer.slice(cursor);
  return (
    <div className="rounded-lg border-2 border-slate-700 bg-[#e8efd6] p-3 font-mono text-slate-900 shadow-inner">
      <div className="mb-1 flex items-center gap-2 text-[9px] font-bold uppercase tracking-wider">
        <span className={shift ? "text-amber-700" : "opacity-30"}>SHIFT</span>
        <span className={alpha ? "text-red-700" : "opacity-30"}>ALPHA</span>
        <span className="ml-auto">{angle === "DEG" ? "D" : angle === "RAD" ? "R" : "G"}</span>
        <span>NORM1</span>
        <span className={memory ? "" : "opacity-30"}>M</span>
      </div>
      <div className="min-h-[1.6em] overflow-x-auto whitespace-nowrap text-base leading-tight">
        {error ? (
          <span className="text-red-700">{error}</span>
        ) : (
          <>
            <span>{before}</span>
            <span className="inline-block w-0.5 animate-pulse bg-slate-900" style={{ height: "1em", verticalAlign: "middle" }} />
            <span>{after}</span>
            {!buffer && <span className="opacity-30">0</span>}
          </>
        )}
      </div>
      <div className="mt-1 min-h-[2em] text-right text-3xl font-bold leading-tight">
        {resultTex ? <InlineMath math={resultTex} /> : <span>{result}</span>}
      </div>
    </div>
  );
}

// ===========================================================================
// Icon-style menu (ClassWiz signature)
// ===========================================================================
function IconMenu({ current, onPick, onClose }: { current: Mode; onPick: (m: Mode) => void; onClose: () => void }) {
  const items: { v: Mode; label: string; icon: string }[] = [
    { v: "COMP", label: "Calculate", icon: "🧮" },
    { v: "STAT", label: "Statistics", icon: "📊" },
    { v: "DIST", label: "Distribution", icon: "🔔" },
    { v: "SHEET", label: "Spreadsheet", icon: "▦" },
    { v: "TABLE", label: "Table", icon: "📋" },
    { v: "EQN", label: "Equation", icon: "𝑥" },
  ];
  return (
    <div className="mt-3 rounded-md border border-slate-500 bg-slate-100 p-3 text-xs text-slate-900 dark:bg-slate-800 dark:text-slate-100">
      <div className="mb-2 flex items-center justify-between">
        <span className="font-semibold">Main Menu</span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-900 dark:hover:text-slate-100">×</button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {items.map((i) => (
          <button key={i.v} onClick={() => onPick(i.v)}
            className={cn("flex flex-col items-center gap-1 rounded-md border-2 p-3 transition",
              current === i.v ? "border-blue-600 bg-blue-50 dark:bg-blue-950" : "border-slate-300 bg-white hover:border-blue-400 dark:border-slate-600 dark:bg-slate-700")}>
            <span className="text-2xl">{i.icon}</span>
            <span className="text-[10px] font-semibold">{i.label}</span>
          </button>
        ))}
      </div>
      <div className="mt-2 text-[10px] text-slate-500 dark:text-slate-400">
        Vector / Inequality / Matrix:{" "}
        <Link to="/scientific-calculator" className="text-blue-600 hover:underline">use Scientific Calculator</Link>
        {" · "}
        BASE-N: <Link to="/binary-calculator" className="text-blue-600 hover:underline">Binary Calculator</Link>
      </div>
    </div>
  );
}

// ===========================================================================
// COMP keypad — slightly different from ES (OPTN, MENU, no MODE)
// ===========================================================================
type KeyDef = { label: string; sub?: string; cls: string };
const KEYS: KeyDef[][] = [
  [
    { label: "SHIFT", cls: "bg-amber-500 text-zinc-900" },
    { label: "ALPHA", cls: "bg-red-600 text-white" },
    { label: "MENU", sub: "SETUP", cls: "bg-blue-700 text-white" },
    { label: "OPTN", cls: "bg-blue-700 text-white" },
    { label: "ON", sub: "OFF", cls: "bg-red-600 text-white" },
  ],
  [
    { label: "x²", sub: "x³", cls: "bg-slate-700 text-white" },
    { label: "□^□", sub: "x⁻¹", cls: "bg-slate-700 text-white" },
    { label: "√", sub: "³√", cls: "bg-slate-700 text-white" },
    { label: "log", sub: "10ˣ", cls: "bg-slate-700 text-white" },
    { label: "ln", sub: "eˣ", cls: "bg-slate-700 text-white" },
  ],
  [
    { label: "sin", sub: "sin⁻¹", cls: "bg-slate-700 text-white" },
    { label: "cos", sub: "cos⁻¹", cls: "bg-slate-700 text-white" },
    { label: "tan", sub: "tan⁻¹", cls: "bg-slate-700 text-white" },
    { label: "(-)", sub: "π", cls: "bg-slate-700 text-white" },
    { label: "RCL", sub: "STO", cls: "bg-slate-700 text-white" },
  ],
  [
    { label: "(", cls: "bg-slate-700 text-white" },
    { label: ")", cls: "bg-slate-700 text-white" },
    { label: ",", cls: "bg-slate-700 text-white" },
    { label: "STO", cls: "bg-slate-700 text-white" },
    { label: "M+", sub: "M-", cls: "bg-slate-700 text-white" },
  ],
  [
    { label: "7", sub: "nPr", cls: "bg-zinc-900 text-white" },
    { label: "8", sub: "nCr", cls: "bg-zinc-900 text-white" },
    { label: "9", cls: "bg-zinc-900 text-white" },
    { label: "DEL", sub: "INS", cls: "bg-red-600 text-white" },
    { label: "AC", sub: "OFF", cls: "bg-red-600 text-white" },
  ],
  [
    { label: "4", cls: "bg-zinc-900 text-white" },
    { label: "5", cls: "bg-zinc-900 text-white" },
    { label: "6", cls: "bg-zinc-900 text-white" },
    { label: "×", cls: "bg-zinc-900 text-white" },
    { label: "÷", cls: "bg-zinc-900 text-white" },
  ],
  [
    { label: "1", cls: "bg-zinc-900 text-white" },
    { label: "2", cls: "bg-zinc-900 text-white" },
    { label: "3", cls: "bg-zinc-900 text-white" },
    { label: "+", cls: "bg-zinc-900 text-white" },
    { label: "-", cls: "bg-zinc-900 text-white" },
  ],
  [
    { label: "0", cls: "bg-zinc-900 text-white" },
    { label: ".", sub: "RND", cls: "bg-zinc-900 text-white" },
    { label: "×10ˣ", sub: "π", cls: "bg-zinc-900 text-white" },
    { label: "Ans", cls: "bg-zinc-900 text-white" },
    { label: "=", sub: "S↔D", cls: "bg-blue-600 text-white" },
  ],
];

function KeyPadEX({ press, shift, alpha }: { press: (l: string) => void; shift: boolean; alpha: boolean }) {
  return (
    <div className="mt-3 space-y-1.5">
      <div className="grid grid-cols-5 gap-1.5">
        {KEYS[0].map((k) => <Btn key={k.label} def={k} press={press} active={(k.label === "SHIFT" && shift) || (k.label === "ALPHA" && alpha)} />)}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        <div />
        <Btn def={{ label: "◀", cls: "bg-blue-700 text-white" }} press={press} />
        <div className="grid grid-rows-2 gap-1.5">
          <Btn def={{ label: "▲", cls: "bg-blue-700 text-white" }} press={press} />
          <Btn def={{ label: "▼", cls: "bg-blue-700 text-white" }} press={press} />
        </div>
        <Btn def={{ label: "▶", cls: "bg-blue-700 text-white" }} press={press} />
        <div />
      </div>
      {KEYS.slice(1).map((row, i) => (
        <div key={i} className="grid grid-cols-5 gap-1.5">
          {row.map((k) => <Btn key={k.label} def={k} press={press} />)}
        </div>
      ))}
    </div>
  );
}

function Btn({ def, press, active }: { def: KeyDef; press: (l: string) => void; active?: boolean }) {
  return (
    <motion.button
      whileTap={{ scale: 0.94, y: 1 }}
      onClick={() => press(def.label)}
      className={cn(
        "relative h-11 rounded-md text-xs font-bold shadow-md sm:h-12 sm:text-sm",
        def.cls,
        active && "ring-2 ring-amber-300 ring-offset-1 ring-offset-slate-500",
      )}
    >
      {def.sub && <span className="absolute left-1 top-0 text-[8px] font-medium text-amber-300">{def.sub}</span>}
      <span>{def.label}</span>
    </motion.button>
  );
}

// ===========================================================================
// EQN
// ===========================================================================
function EqnPanel({ onExit }: { onExit: () => void }) {
  const [kind, setKind] = useState<EqnKind>("quad");
  const [coefs, setCoefs] = useState<Record<string, string>>({});
  const num = (k: string) => Number(coefs[k] ?? "0") || 0;
  const result = useMemo(() => {
    if (kind === "quad") return solveQuadratic(num("a"), num("b"), num("c"));
    if (kind === "cubic") return solveCubic(num("a"), num("b"), num("c"), num("d"));
    if (kind === "lin2") return solveLin2(num("a1"), num("b1"), num("c1"), num("a2"), num("b2"), num("c2"));
    return solveLin3([
      [num("a1"), num("b1"), num("c1"), num("d1")],
      [num("a2"), num("b2"), num("c2"), num("d2")],
      [num("a3"), num("b3"), num("c3"), num("d3")],
    ]);
  }, [kind, coefs]);
  const fields: Record<EqnKind, string[]> = {
    quad: ["a", "b", "c"],
    cubic: ["a", "b", "c", "d"],
    lin2: ["a1", "b1", "c1", "a2", "b2", "c2"],
    lin3: ["a1", "b1", "c1", "d1", "a2", "b2", "c2", "d2", "a3", "b3", "c3", "d3"],
  };
  return (
    <SubPanel title="Equation Solver" onExit={onExit}>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {([
          { v: "lin2", label: "2 unknowns" },
          { v: "lin3", label: "3 unknowns" },
          { v: "quad", label: "Quadratic" },
          { v: "cubic", label: "Cubic" },
        ] as { v: EqnKind; label: string }[]).map((k) => (
          <button key={k.v} onClick={() => { setKind(k.v); setCoefs({}); }}
            className={cn("rounded px-2 py-1", kind === k.v ? "bg-blue-600 text-white" : "bg-slate-200 dark:bg-slate-700")}>
            {k.label}
          </button>
        ))}
      </div>
      <div className={cn("grid gap-2", kind === "lin3" ? "grid-cols-4" : "grid-cols-3")}>
        {fields[kind].map((f) => (
          <label key={f} className="flex flex-col gap-0.5">
            <span className="font-mono text-[10px] opacity-70">{f}</span>
            <input type="number" value={coefs[f] ?? ""} onChange={(e) => setCoefs((c) => ({ ...c, [f]: e.target.value }))}
              className="h-8 rounded bg-white px-2 font-mono dark:bg-slate-700" />
          </label>
        ))}
      </div>
      <div className="mt-3 rounded border border-slate-400 bg-white p-2 dark:border-slate-600 dark:bg-slate-900">
        {result.ok ? (
          <ul className="space-y-1">{result.pretty.map((p, i) => <li key={i}><InlineMath math={p} /></li>)}</ul>
        ) : <p className="text-red-500">{result.message}</p>}
      </div>
    </SubPanel>
  );
}

// ===========================================================================
// TABLE
// ===========================================================================
function TablePanel({ angle, onExit }: { angle: AngleMode; onExit: () => void }) {
  const [expr, setExpr] = useState("X^2");
  const [start, setStart] = useState("0");
  const [end, setEnd] = useState("4");
  const [step, setStep] = useState("1");
  const rows = useMemo(
    () => generateTable(expr, Number(start) || 0, Number(end) || 0, Math.max(1e-6, Number(step) || 1), angle),
    [expr, start, end, step, angle],
  );
  return (
    <SubPanel title="Function Table" onExit={onExit}>
      <label className="mb-2 block">
        <span className="text-[10px] opacity-70">f(X) =</span>
        <input value={expr} onChange={(e) => setExpr(e.target.value)} className="h-8 w-full rounded bg-white px-2 font-mono dark:bg-slate-700" />
      </label>
      <div className="mb-2 grid grid-cols-3 gap-2">
        <Field label="Start" value={start} onChange={setStart} />
        <Field label="End" value={end} onChange={setEnd} />
        <Field label="Step" value={step} onChange={setStep} />
      </div>
      <div className="max-h-56 overflow-auto rounded border border-slate-400 bg-white dark:border-slate-600 dark:bg-slate-900">
        <table className="w-full font-mono text-xs">
          <thead className="sticky top-0 bg-slate-100 dark:bg-slate-800"><tr><th className="px-2 py-1 text-left">X</th><th className="px-2 py-1 text-left">f(X)</th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-2 py-1">{r.x}</td>
                <td className="px-2 py-1">{r.y === null ? "ERROR" : formatNumber(r.y)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SubPanel>
  );
}

// ===========================================================================
// STAT (1-var)
// ===========================================================================
function StatPanel({ onExit }: { onExit: () => void }) {
  const [data, setData] = useState("1, 2, 3, 4, 5");
  const stats = useMemo(() => {
    const xs = data.split(/[\s,]+/).map(Number).filter(Number.isFinite);
    if (xs.length === 0) return null;
    const sum = xs.reduce((a, b) => a + b, 0);
    const mean = sum / xs.length;
    const variance = xs.reduce((a, b) => a + (b - mean) ** 2, 0) / xs.length;
    return { n: xs.length, sum, mean, sd: Math.sqrt(variance), min: Math.min(...xs), max: Math.max(...xs) };
  }, [data]);
  return (
    <SubPanel title="Statistics — 1 Variable" onExit={onExit}>
      <textarea value={data} onChange={(e) => setData(e.target.value)} rows={3}
        className="w-full rounded bg-white p-2 font-mono dark:bg-slate-700" />
      {stats && (
        <dl className="mt-2 grid grid-cols-3 gap-2 font-mono text-xs">
          <Stat label="n" value={stats.n} />
          <Stat label="Σx" value={stats.sum} />
          <Stat label="x̄" value={stats.mean} />
          <Stat label="σx" value={stats.sd} />
          <Stat label="min" value={stats.min} />
          <Stat label="max" value={stats.max} />
        </dl>
      )}
    </SubPanel>
  );
}

// ===========================================================================
// DISTRIBUTION (EX-only)
// ===========================================================================
function DistributionPanel({ onExit }: { onExit: () => void }) {
  const [kind, setKind] = useState<DistKind>("normal");
  // Normal
  const [mu, setMu] = useState("0"); const [sigma, setSigma] = useState("1");
  const [a, setA] = useState("-1.96"); const [b, setB] = useState("1.96");
  // Binomial
  const [n, setN] = useState("10"); const [p, setP] = useState("0.5"); const [k, setK] = useState("5");
  // Poisson
  const [lambda, setLambda] = useState("3"); const [pk, setPk] = useState("2");
  // t
  const [tVal, setTVal] = useState("1.96"); const [df, setDf] = useState("10");

  const result = useMemo(() => {
    if (kind === "normal") {
      const va = Number(a), vb = Number(b), mm = Number(mu), ss = Number(sigma);
      const pr = normalCdfRange(va, vb, mm, ss);
      return { tex: `P(${formatNumber(va)} \\le X \\le ${formatNumber(vb)}) = ${formatNumber(pr)}` };
    }
    if (kind === "binomial") {
      const nn = Math.floor(Number(n)), pp = Number(p), kk = Math.floor(Number(k));
      const pmf = binomialPmf(nn, pp, kk);
      const cdf = binomialCdf(nn, pp, kk);
      return { tex: `P(X = ${kk}) = ${formatNumber(pmf)} \\quad P(X \\le ${kk}) = ${formatNumber(cdf)}` };
    }
    if (kind === "poisson") {
      const ll = Number(lambda), kk = Math.floor(Number(pk));
      return { tex: `P(X = ${kk}) = ${formatNumber(poissonPmf(ll, kk))}` };
    }
    const tt = Number(tVal), dd = Number(df);
    return { tex: `P(T \\le ${formatNumber(tt)}) = ${formatNumber(tCdf(tt, dd))}` };
  }, [kind, mu, sigma, a, b, n, p, k, lambda, pk, tVal, df]);

  return (
    <SubPanel title="Distribution" onExit={onExit}>
      <div className="mb-2 flex flex-wrap gap-1.5 text-xs">
        {([
          { v: "normal", label: "Normal" },
          { v: "binomial", label: "Binomial" },
          { v: "poisson", label: "Poisson" },
          { v: "t", label: "Student t" },
        ] as { v: DistKind; label: string }[]).map((d) => (
          <button key={d.v} onClick={() => setKind(d.v)}
            className={cn("rounded px-2 py-1", kind === d.v ? "bg-blue-600 text-white" : "bg-slate-200 dark:bg-slate-700")}>
            {d.label}
          </button>
        ))}
      </div>
      {kind === "normal" && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Field label="μ" value={mu} onChange={setMu} />
          <Field label="σ" value={sigma} onChange={setSigma} />
          <Field label="lower a" value={a} onChange={setA} />
          <Field label="upper b" value={b} onChange={setB} />
        </div>
      )}
      {kind === "binomial" && (
        <div className="grid grid-cols-3 gap-2">
          <Field label="n" value={n} onChange={setN} />
          <Field label="p" value={p} onChange={setP} />
          <Field label="k" value={k} onChange={setK} />
        </div>
      )}
      {kind === "poisson" && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="λ" value={lambda} onChange={setLambda} />
          <Field label="k" value={pk} onChange={setPk} />
        </div>
      )}
      {kind === "t" && (
        <div className="grid grid-cols-2 gap-2">
          <Field label="t" value={tVal} onChange={setTVal} />
          <Field label="df" value={df} onChange={setDf} />
        </div>
      )}
      <div className="mt-3 rounded border border-slate-400 bg-white p-3 dark:border-slate-600 dark:bg-slate-900">
        <InlineMath math={result.tex} />
      </div>
    </SubPanel>
  );
}

// ===========================================================================
// SPREADSHEET (basic 5×5)
// ===========================================================================
function SpreadsheetPanel({ onExit }: { onExit: () => void }) {
  const ROWS = 5, COLS = 5;
  const [cells, setCells] = useState<string[][]>(() => Array.from({ length: ROWS }, () => Array(COLS).fill("")));
  const [active, setActive] = useState<[number, number]>([0, 0]);

  const evaluated = useMemo(() => {
    const out: (number | string)[][] = cells.map((row) => row.map((v) => v));
    // Up to 3 passes for cell references like =A1+B2
    for (let pass = 0; pass < 3; pass++) {
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const raw = cells[r][c]?.trim() ?? "";
          if (raw.startsWith("=")) {
            let expr = raw.slice(1);
            // Replace cell refs (A1..E5) with current values
            expr = expr.replace(/([A-E])([1-5])/g, (_m, col: string, row: string) => {
              const ci = "ABCDE".indexOf(col);
              const ri = parseInt(row, 10) - 1;
              const v = out[ri][ci];
              return typeof v === "number" ? `(${v})` : "0";
            });
            const res = evaluate(expr, { angle: "RAD", ans: 0, vars: {} });
            out[r][c] = res.ok ? res.value : "ERR";
          } else if (raw === "") {
            out[r][c] = "";
          } else {
            const n = Number(raw);
            out[r][c] = Number.isFinite(n) ? n : raw;
          }
        }
      }
    }
    return out;
  }, [cells]);

  return (
    <SubPanel title="Spreadsheet — 5×5" onExit={onExit}>
      <p className="mb-2 text-[10px] opacity-70">Type a number, or start with <code>=</code> for a formula. References: A1..E5. Example: <code>=A1+B1</code></p>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead><tr><th /> {Array.from({ length: COLS }).map((_, c) => (
            <th key={c} className="px-2 py-1 text-center font-mono">{"ABCDE"[c]}</th>
          ))}</tr></thead>
          <tbody>
            {cells.map((row, r) => (
              <tr key={r}>
                <th className="px-2 py-1 font-mono">{r + 1}</th>
                {row.map((v, c) => (
                  <td key={c} className="p-0.5">
                    <input
                      value={active[0] === r && active[1] === c ? v : (typeof evaluated[r][c] === "number" ? formatNumber(evaluated[r][c] as number) : String(evaluated[r][c] ?? ""))}
                      onFocus={() => setActive([r, c])}
                      onChange={(e) => {
                        const next = cells.map((row) => row.slice());
                        next[r][c] = e.target.value;
                        setCells(next);
                      }}
                      className="h-8 w-20 rounded bg-white px-1 font-mono text-xs dark:bg-slate-700"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SubPanel>
  );
}

// ===========================================================================
// Shared helpers
// ===========================================================================
function SubPanel({ title, onExit, children }: { title: string; onExit: () => void; children: React.ReactNode }) {
  return (
    <div className="mt-3 rounded-md border border-slate-500 bg-slate-100 p-3 text-slate-900 dark:bg-slate-800 dark:text-slate-100">
      <div className="mb-2 flex items-center justify-between text-xs">
        <span className="font-semibold">{title}</span>
        <button onClick={onExit} className="rounded bg-slate-300 px-2 py-0.5 hover:bg-slate-200 dark:bg-slate-700">← COMP</button>
      </div>
      <div className="text-xs">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (s: string) => void }) {
  return (
    <label className="block">
      <span className="text-[10px] opacity-70">{label}</span>
      <input value={value} type="number" onChange={(e) => onChange(e.target.value)}
        className="h-8 w-full rounded bg-white px-2 font-mono dark:bg-slate-700" />
    </label>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-slate-300 bg-white p-2 dark:border-slate-600 dark:bg-slate-900">
      <div className="text-[10px] opacity-70">{label}</div>
      <div>{formatNumber(value)}</div>
    </div>
  );
}

// ===========================================================================
// Educational content
// ===========================================================================
function Content() {
  const lang = useAppStore((s) => s.lang);
  const en = lang === "en";
  return (
    <div className="mt-10 space-y-8">
      <Section title={en ? "About the Casio FX-991EX ClassWiz" : "ক্যাসিও FX-991EX ClassWiz সম্পর্কে"}>
        <p>{en
          ? "Released in 2014, the FX-991EX is the flagship of Casio's ClassWiz series. It introduced a 192×63 high-resolution dot-matrix LCD, a CPU around four times faster than the FX-991ES Plus, an icon-based menu, Spreadsheet mode, Distribution mode, and built-in QR code generation."
          : "২০১৪ সালে প্রকাশিত FX-991EX হল Casio এর ClassWiz সিরিজের ফ্ল্যাগশিপ। এতে রয়েছে ১৯২×৬৩ উচ্চ-রেজোলিউশন LCD, ৪ গুণ দ্রুত CPU, আইকনভিত্তিক মেনু, স্প্রেডশিট, ডিস্ট্রিবিউশন ও QR কোড।"}</p>
      </Section>
      <Section title={en ? "FX-991EX vs FX-991ES Plus" : "FX-991EX বনাম FX-991ES Plus"}>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-xs">
            <thead className="bg-muted text-muted-foreground"><tr><th className="px-2 py-1 text-left">Feature</th><th className="px-2 py-1 text-left">FX-991ES Plus</th><th className="px-2 py-1 text-left">FX-991EX (ClassWiz)</th></tr></thead>
            <tbody>
              {[
                ["Display", "96×31 dot", "192×63 dot (HD)"],
                ["Functions", "417", "552"],
                ["CPU speed", "1×", "≈ 4× faster"],
                ["Menu", "Numeric mode menu", "Icon-based menu"],
                ["Spreadsheet", "—", "✓ (5×45 grid)"],
                ["Distribution", "—", "✓ Normal, Binomial, Poisson"],
                ["QR code output", "—", "✓"],
                ["Equation solver", "Up to cubic / 3 vars", "Up to quartic / 4 vars"],
                ["Power", "Solar + LR44", "Solar + LR44"],
                ["Approx. price (BD)", "Lower", "Higher"],
              ].map((r) => (
                <tr key={r[0]} className="border-t border-border">
                  <td className="px-2 py-1 font-semibold">{r[0]}</td>
                  <td className="px-2 py-1">{r[1]}</td>
                  <td className="px-2 py-1">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title={en ? "New features in EX" : "EX এ নতুন ফিচার"}>
        <ul className="ml-5 list-disc space-y-1">
          <li><b>{en ? "High-resolution display" : "উচ্চ-রেজোলিউশন ডিসপ্লে"}</b> — {en ? "fractions, surds and matrices render almost like a textbook." : "ভগ্নাংশ ও করণী পাঠ্যবইয়ের মতো দেখায়।"}</li>
          <li><b>{en ? "Spreadsheet mode" : "স্প্রেডশিট মোড"}</b> — {en ? "5 columns × 45 rows of cells with cell references and basic formulas." : "৫ কলাম × ৪৫ সারি, সেল রেফারেন্স ও ফর্মুলা সহ।"}</li>
          <li><b>{en ? "Distribution mode" : "ডিস্ট্রিবিউশন মোড"}</b> — {en ? "Normal, Binomial, Poisson with PDF and CDF." : "নরমাল, বাইনোমিয়াল, পয়সঁ — PDF ও CDF।"}</li>
          <li><b>{en ? "QR code output" : "QR কোড আউটপুট"}</b> — {en ? "share the current calculation by scanning a QR code." : "QR কোড স্ক্যান করে ফলাফল শেয়ার করুন।"}</li>
          <li><b>OPTN</b> — {en ? "context-sensitive options menu, varies per mode." : "প্রতিটি মোডে আলাদা অপশন মেনু।"}</li>
        </ul>
      </Section>
      <Section title={en ? "ClassWiz icon menu navigation" : "ClassWiz আইকন মেনু"}>
        <p>{en ? "Press MENU to open a 4×3 grid of icons. Use arrow keys (or tap) to highlight a mode and = to enter it. The icon-based menu makes mode switching faster than the older numeric menu." : "MENU চাপলে আইকনের গ্রিড খোলে। তীর কী দিয়ে মোড নির্বাচন করুন এবং = চাপুন। নতুন মেনু পুরোনোটির চেয়ে দ্রুত।"}</p>
      </Section>
      <Section title={en ? "Distribution mode tutorial" : "ডিস্ট্রিবিউশন মোড টিউটোরিয়াল"}>
        <ol className="ml-5 list-decimal space-y-1">
          <li>{en ? "Open MENU → Distribution." : "MENU → Distribution।"}</li>
          <li>{en ? "Choose a distribution: Normal, Binomial, Poisson, t." : "ডিস্ট্রিবিউশন বেছে নিন।"}</li>
          <li>{en ? "Enter parameters (μ, σ for Normal; n, p, k for Binomial; λ, k for Poisson)." : "প্যারামিটার দিন।"}</li>
          <li>{en ? "Read the probability — both PMF and CDF where applicable." : "সম্ভাবনা পড়ুন — PMF ও CDF।"}</li>
        </ol>
        <p className="mt-1">{en ? "Example: Normal μ=0, σ=1, P(X ≤ 1.96) ≈ 0.975 — the 97.5% quantile." : "উদাহরণ: নরমাল μ=0, σ=1, P(X ≤ 1.96) ≈ 0.975।"}</p>
      </Section>
      <Section title={en ? "Spreadsheet mode" : "স্প্রেডশিট মোড"}>
        <p>{en
          ? "Use cell references A1..E5 in formulas (start a cell with =). Useful for repetitive calculations: enter a list of values, then an =SUM-style formula. Our online version implements 5×5 with arithmetic and standard math functions."
          : "ফর্মুলায় সেল রেফারেন্স A1..E5 ব্যবহার করুন (সেল = দিয়ে শুরু)। পুনরাবৃত্ত হিসাবের জন্য সুবিধাজনক।"}</p>
      </Section>
      <Section title={en ? "Why some students prefer EX" : "কেন কেউ কেউ EX পছন্দ করেন"}>
        <p>{en ? "Faster, sharper display, more functions, distribution and spreadsheet modes, QR sharing — particularly useful for university courses that lean on statistics or numerical work." : "দ্রুত, ঝকঝকে ডিসপ্লে, বেশি ফাংশন, ডিস্ট্রিবিউশন/স্প্রেডশিট — বিশেষত পরিসংখ্যান-নির্ভর কোর্সে।"}</p>
      </Section>
      <Section title={en ? "Why some prefer ES Plus" : "কেন কেউ কেউ ES Plus পছন্দ করেন"}>
        <p>{en
          ? "Cheaper, ubiquitous in Bangladesh classrooms, accepted in every approved-calculator list, and fully sufficient for SSC and HSC mathematics. Muscle memory matters in timed exams."
          : "সাশ্রয়ী, বাংলাদেশের ক্লাসরুমে সর্বত্র এবং SSC/HSC এর জন্য যথেষ্ট। সময়সীমিত পরীক্ষায় বোতামের অভ্যাস গুরুত্বপূর্ণ।"}</p>
        <p>
          <Link to="/casio-fx-991es" className="text-accent hover:underline">{en ? "Open FX-991ES Plus emulator →" : "FX-991ES Plus এমুলেটর →"}</Link>
        </p>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const lang = useAppStore((s) => s.lang);
  return (
    <section className={cn(lang === "bn" && "font-bn")}>
      <h2 className="mb-2 text-lg font-bold">{title}</h2>
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}
